/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import Picker from 'emoji-picker-react';
import UncontrolledTextInput from 'app/components/common/UncontrolledTextInput.jsx';
import StyledSelect from 'app/components/common/StyledSelect.jsx';
import * as IconFactory from 'app/components/common/IconFactory.jsx';
import ConstantMixin from 'app/mixins/ConstantMixin.js';
import LabelLibrary from 'app/components/PostingTool/LabelLibrary.js';
import PostToolUploader from 'app/components/PostingTool/PostToolUploader.jsx';
import TextareaEvents from 'app/components/PostingTool/TextareaEvents.js';
import ThumbnailPreview from 'app/components/PostingTool/ThumbnailPreview.jsx';
import LinkPreview from 'app/components/PostingTool/LinkPreview.jsx';
import GoogleSchedule from 'app/components/PostingTool/GoogleSchedule.jsx';
import { outputAccountNetwork } from '@/lib/output-account.ts';
import { initialsFromName, OutputAvatar } from '@/shadcn/components/NewAutomation/OutputTile.tsx';
import * as _ from 'underscore';
import { Breakpoint } from 'react-socks';
import ToggleControl from 'app/components/common/ToggleControl.jsx';
import HelpButton from 'app/components/common/HelpButton.jsx';
import MediaSourceMenu from 'app/components/PostingTool/MediaSourceMenu.jsx';
import HashtagGenerator from 'app/components/PostingTool/Accessories/HashtagGenerator.jsx';
import TagInput from 'app/components/common/TagInput.jsx';
import YouTubePlaylistChooser from 'app/components/PostingTool/Accessories/YouTubePlaylistChooser.jsx';
import axios from 'axios';

/*
 * A QR code only ever stands in for a link, so with no link in the post there is
 * nothing to offer. The theme only matters once a code is being made AND there is no
 * image of the post's own — a code stamped into a corner carries no background to
 * theme, so offering the choice there would be offering nothing. imageLink counts as
 * an image: the posting path substitutes it for mb_id and builds the post's media
 * from it, so a link's own picture is what the code gets stamped onto.
 */
const hasOwnImage = (postData) => {
    return !!(postData.mb_id || postData.imageLink || (postData.hashes !== undefined && postData.hashes.length));
};

// Unset reads as dark, which is what the server posts
const isDarkQr = (postData) => {
    return postData.postDarkQR === undefined || postData.postDarkQR === null || postData.postDarkQR === ''
        ? true
        : (parseInt(postData.postDarkQR, 10) ? true : false);
};

class ServiceFields extends React.Component {
    constructor(props) {
        super(props);

        let constants = this.props.app.config.constants;

        this.state = {
            isEmojiPickerVisible: false,
            tikTokDisclosure: false,
            youtubeCategoriesLoaded: false,
            isHashtagSelectorVisible: false,
            qrCard: null
        };

        this.keyUpPasteFlag = false;
        this.keyUpTimeout = 0;
        this.instanceName = "ServiceFields";
        this.qrCardRequested = '';
        this.qrCardTimeout = 0;

        this.messageRef = React.createRef();
        this.emojiPickerRef = React.createRef();
        this.fieldGenerator = this.fieldGenerator.bind(this);
        this.interactionCheck = this.interactionCheck.bind(this);
        this.toggleEmojiPicker = this.toggleEmojiPicker.bind(this);
        this.toggleTikTokDisclosure = this.toggleTikTokDisclosure.bind(this);
        this.setDropzoneRef = this.setDropzoneRef.bind(this);
        this.toggleHashtagSelector = this.toggleHashtagSelector.bind(this);
        this.toggleFacebookMediaOptions = this.toggleFacebookMediaOptions.bind(this);
        this.mediaChecker = this.mediaChecker.bind(this);

    }

    componentDidMount() {

        document.addEventListener('mouseup', this.interactionCheck);
        document.addEventListener('touchend', this.interactionCheck);
        this.syncQrCard();

        let constants = this.props.app.config.constants;
        if (parseInt(this.props.serviceType) === constants.POST_SVC_YOUTUBE) {
            let id = this.props.selectedSocials[constants.POST_SVC_YOUTUBE][0];
            if (Object.keys(this.props.socials.categories).length === 0) {
                this.props.getYouTubeCategories(id);
            }
            if (this.props.socials.playlists[id] === undefined) {
                this.props.getYouTubePlaylists(id);
            }
        }

    }

    componentDidUpdate(prevProps, prevState) {
        let constants = this.props.app.config.constants;

        if (prevProps && prevProps.app.loading.SOCIAL_GET_CATEGORIES === true && this.props.app.loading.SOCIAL_GET_CATEGORIES === false) {
            this.setState({
                youtubeCategoriesLoaded: true
            });
        }

        if (prevProps && prevProps.postData !== undefined) {
            if (prevProps.postData.message && this.messageRef.current) {
                this.messageRef.current.value = this.props.postData.message;
            }
        }

        if (parseInt(this.props.serviceType) === constants.POST_SVC_YOUTUBE) {
            if (prevProps.selectedSocials[constants.POST_SVC_YOUTUBE][0] !== this.props.selectedSocials[constants.POST_SVC_YOUTUBE][0]) {
                this.props.getYouTubePlaylists(this.props.selectedSocials[constants.POST_SVC_YOUTUBE][0]);
            }
        }

        this.syncQrCard();

    }

    componentWillUnmount() {

        document.removeEventListener('mouseup', this.interactionCheck);
        document.removeEventListener('touchend', this.interactionCheck);
        clearTimeout(this.qrCardTimeout);
        this.qrCardRequested = '';

    }

    /*
     * What the X preview's QR card is drawn from, or '' when X will not post one: no link,
     * the code off, or an image of the post's own for the code to be stamped onto instead.
     * Waits out a link that is still loading, since its picture, once it arrives, becomes
     * the post's image (addLinkInfoToPostData) and there is no card after all.
     */
    qrCardKey() {
        let constants = this.props.app.config.constants,
            postData = this.props.postData;

        if (parseInt(this.props.serviceType) !== constants.POST_SVC_TWITTER ||
            !postData || !postData.link || !postData.postQrCode ||
            hasOwnImage(postData) || this.props.isLinkLoading) {
            return '';
        }

        let info = this.props.linkPreviews[postData.link];

        return JSON.stringify([postData.link, info && info.title ? info.title : '', isDarkQr(postData) ? 1 : 0]);
    }

    /*
     * Ask the server for the card whenever what it is drawn from changes. The server draws it
     * with the same code the post is made with, so the preview is the card X gets rather than
     * an imitation of it. Debounced, because the title and theme can change in quick
     * succession and each card is an image render.
     */
    syncQrCard() {
        let key = this.qrCardKey();

        if (key === this.qrCardRequested) {
            return;
        }

        this.qrCardRequested = key;
        clearTimeout(this.qrCardTimeout);

        if (!key) {
            return;
        }

        this.qrCardTimeout = setTimeout(() => {
            let [link, title, dark] = JSON.parse(key),
                formData = new FormData();

            formData.append('link', link);
            formData.append('title', title);
            formData.append('dark', dark);

            axios({
                url: `${this.props.app.config.apiUrl}/post/qrPreview`,
                method: 'POST',
                data: formData,
                withCredentials: true,
                responseType: 'json'
            })
            .then((response) => {
                if (this.qrCardRequested === key) {
                    this.setState({qrCard: {key: key, image: response.data.image || ''}});
                }
            })
            .catch(() => {
                // No card rather than a broken one; the post itself is unaffected
                if (this.qrCardRequested === key) {
                    this.setState({qrCard: {key: key, image: ''}});
                }
            });
        }, 400);
    }

    interactionCheck(e) {
        if (this.state.isEmojiPickerVisible) {
            if (this.emojiPickerRef.current && this.emojiPickerRef.current.contains(e.target)) {
                return;
            }
            this.setState({
                isEmojiPickerVisible: false
            });
        }
    }

    setDropzoneRef(ref) {
        this.setState({
            dropzoneRef: ref
        });
    }

    fieldGenerator(key) {
        let fieldInfo = this.props.serviceInfo.fields[key],
            field = null,
            type = this.props.serviceType,
            postData = null,
            validator = this.props.posts.postValidators[type],
            validationErrors = this.props.validationErrors[type] || null,
            mediaErrors = this.props.mediaErrors[type] || null,
            hasError = false,
            hasImageError = false,
            errorClass = '',
            imageErrorClass = '',
            constants = this.props.app.config.constants,
            mediaSourceOptions = [];

        if (this.props.postData !== undefined) {
            postData = this.props.postData;
        }

        this.props.mediaSourceOptions.map((sourceOption) => {
            let option = Object.assign({}, sourceOption);

            switch (option.key) {
                case 'upload':
                    option.action = () => {
                        this.props.showMediaEditor('mediaUploader', this.props.serviceType);
                    };
                    break;

                case 'unsplash':
                    option.action = () => {
                        this.props.showMediaEditor('galleryChooser', this.props.serviceType);
                    };
                    break;

            }

            mediaSourceOptions.push(option);

        });

        let categoryButtonClass = "tt-button tag-button",
            categoryName = '';
        if (postData.tagID !== undefined && postData.tagID) {
            categoryButtonClass += " active";
            if (postData.tagName) {
                categoryName = postData.tagName;
            }
        }

        const hashtagCallback = (tag, action, service) => {
            let message = this.props.onHashtagSelect(tag, action, service);

            if (this.messageRef.current) {
                this.messageRef.current.value = message;
            }

        };

        let hashtagButtonClass = 'tt-button hashtags',
            hashtagSelector = null;
        if (this.state.isHashtagSelectorVisible) {
            hashtagButtonClass += ' active';
            hashtagSelector = (
                <HashtagGenerator
                    {...this.props}
                    postData={postData}
                    onHashtagSelect={hashtagCallback}
                    service={this.props.serviceType}/>
            );
        }

        const emojiCallback = (e, emoji) => {
            if (this.messageRef.current) {
                let startPos = this.messageRef.current.selectionStart,
                    endPos = this.messageRef.current.selectionEnd,
                    inputChange = {
                    currentTarget: {
                        dataset: {
                            field: `postData.services.${this.props.serviceType}.message`
                        }
                    },
                    target: {
                        value: ''
                    }
                };

                inputChange.target.value = `${this.messageRef.current.value.substring(0, startPos)} ${emoji.emoji} ${this.messageRef.current.value.substring(endPos, this.messageRef.current.value.length)}`;

                this.props.requestChange(inputChange);

                this.messageRef.current.value = inputChange.target.value;
            }
        }

        if (fieldInfo) {
            if (validationErrors !== null && validationErrors[key] !== undefined && validationErrors[key]) {
                hasError = true;
                errorClass = 'error';
            }

            // since mb_id is output with the message field check for any errors specifically instead of
            // waiting for the loop
            if (key === 'message' && mediaErrors !== null && mediaErrors.mb_id !== undefined && mediaErrors.mb_id) {
                hasImageError = true;
            }

            switch (fieldInfo.type) {
                case 'form_name':
                    return field;

                case 'url':
                    // only show the url field for specific services
                    if ((parseInt(this.props.serviceType) === constants.POST_SVC_GMYBUSINESS &&
                        postData.cta_type !== undefined && ['ACTION_TYPE_UNSPECIFIED', 'CALL'].indexOf(postData.cta_type) === -1) ||
                        (parseInt(this.props.serviceType) === constants.POST_SVC_REDDIT && postData.form !== undefined && postData.form === 'link_post') ||
                        [constants.POST_SVC_SLACK, constants.POST_SVC_PINTEREST, constants.POST_SVC_TUMBLR].indexOf(parseInt(this.props.serviceType)) !== -1) {
                        if ([constants.POST_SVC_SLACK, constants.POST_SVC_REDDIT, constants.POST_SVC_PINTEREST, constants.POST_SVC_TUMBLR].indexOf(parseInt(this.props.serviceType)) !== -1) {
                            let linkLoader = null;
                            if (postData[key] && this.props.linkPreviews[postData[key]] === undefined) {
                                linkLoader = (
                                    <div className="link-field-loader">
                                        <div className="working">
                                            <i className="fa fa-circle-o-notch fa-spin"></i>
                                        </div>
                                    </div>
                                );
                            }
                            field = (
                                <div className={`link-field service-${key}`}>
                                    <div className="pt-field-label">{LabelLibrary.getStringsForField(this.props.serviceType, key, 'placeholder')}</div>
                                    {linkLoader}
                                    <UncontrolledTextInput
                                        className={`text-input large ${errorClass}`}
                                        value={postData[key] !== undefined ? postData[key] : ''}
                                        name={key}
                                        onChange={this.props.requestChange}
                                        data-field={`postData.services.${type}.${key}`}
                                        data-servicetype={this.props.serviceType}
                                        onPaste={TextareaEvents.onTextareaPaste.bind(this)}
                                        onKeyUp={TextareaEvents.onTextareaKeyUp.bind(this)}/>
                                </div>
                            );
                        } else {
                            field = (
                                <div className={`service-${key}`}>
                                    <div className="pt-field-label">{LabelLibrary.getStringsForField(this.props.serviceType, key, 'placeholder')}</div>
                                    <UncontrolledTextInput
                                        className={`text-input large ${errorClass}`}
                                        value={postData[key] !== undefined ? postData[key] : ''}
                                        onChange={this.props.requestChange}
                                        data-field={`postData.services.${type}.${key}`}/>
                                </div>
                            );
                        }
                    }
                    return field;

                case 'attachment':
                    if ((parseInt(type) === constants.POST_SVC_REDDIT && postData.form === 'image_post') ||
                        (parseInt(type) === constants.POST_SVC_SNAPCHAT && postData.form === 'story') ||
                        (parseInt(type) === constants.POST_SVC_TIKTOK && postData.form === 'video_post') ||
                        ([constants.POST_SVC_YOUTUBE].indexOf(parseInt(type)) !== -1)) {

                        if ((postData.imageLink !== undefined && postData.imageLink) &&
                            (postData.mb_id === undefined || !postData.mb_id)) {
                            return (
                                <ThumbnailPreview
                                    {...this.props}
                                    serviceType={this.props.serviceType}
                                    hasError={hasImageError}
                                    imageLink={postData.imageLink}
                                    editMode='network'/>
                            );
                        } else if ((postData.imageLink === undefined || !postData.imageLink) &&
                                    (postData.mb_id === undefined || !postData.mb_id) &&
                                    (postData.hashes === undefined || postData.hashes.length === 0)) {
                            return (
                                <PostToolUploader
                                    serviceType={this.props.serviceType}
                                    context="service"
                                    hasError={hasImageError}
                                    postData={postData}
                                    {...this.props}/>
                            );
                        } else {
                            let mb_id  = (postData.mb_id !== undefined && postData.mb_id) ? postData.mb_id : 0,
                                hashes = (postData.hashes !== undefined && postData.hashes.length) ? postData.hashes : [];
                            return (
                                <ThumbnailPreview
                                    {...this.props}
                                    serviceType={this.props.serviceType}
                                    mb_id={mb_id}
                                    hashes={hashes}
                                    hasError={hasImageError}
                                    editMode='network'/>
                            );
                        }
                    }
                    break;

                case 'text':
                    if (key === 'message') {
                        let serviceImage = null,
                            linkAlert = null;

                        if (parseInt(type) === constants.POST_SVC_TIKTOK && postData.form === 'video_post') {
                            // handled by attachment when a video is present - no message
                            // box shall be rendered
                            return null;
                        }

                        if (parseInt(type) !== constants.POST_SVC_REDDIT) {
                            let hashes = (postData && postData.hashes !== undefined && postData.hashes.length) ? postData.hashes: [],
                                mb_id = (postData && postData.mb_id !== undefined && postData.mb_id) ? postData.mb_id : 0;

                            if (postData !== undefined && (postData.mb_id !== undefined && postData.mb_id || postData.hashes !== undefined && postData.hashes.length)) {
                                serviceImage = (
                                    <ThumbnailPreview
                                        {...this.props}
                                        hasError={hasImageError}
                                        hashes={hashes}
                                        mb_id={mb_id}
                                        editMode='network'/>
                                );
                            }  else if (postData.imageLink !== undefined && postData.imageLink) {
                                serviceImage = (
                                    <ThumbnailPreview
                                        {...this.props}
                                        hasError={hasImageError}
                                        imageLink={postData.imageLink}
                                        editMode='network'/>
                                );
                            } else if (validator.fields['mb_id'] !== undefined) {
                                serviceImage = (
                                    <PostToolUploader
                                        serviceType={this.props.serviceType}
                                        context="service"
                                        postData={postData}
                                        hasError={hasImageError}
                                        {...this.props}/>
                                );
                            }
                        }

                        // RSS Output? NO IMAGE FOR YOU!!
                        if (parseInt(type) === constants.POST_SVC_FEED) {
                            serviceImage = null;
                        }

                        if ([constants.POST_SVC_TWITTER, constants.POST_SVC_MASTODON].indexOf(parseInt(type)) !== -1) {
                            if (postData.link) {
                                linkAlert = <span style={{"fontSize": "0.7em"}}>(with link)</span>;
                            }
                        }

                        let aiHelperButton = null;
                        if (postData.message !== undefined && postData.message.trim() !== '') {
                            aiHelperButton = (
                                <div className={"tt-button ai-helper"} role="button" aira-label="Edit content with AI" onClick={() => {
                                    this.props.showAIHelper('prompted', type);
                                }}>
                                    <i className="icon ion-ios-color-wand"/> <div className="new-tag">Edit with AI</div>
                                </div>
                            );
                        }

                        return (
                            <div className="service-message">
                                <Breakpoint desktop only>
                                    <div className="message-group">
                                        <textarea
                                            ref={this.messageRef}
                                            className={`text-area large ${errorClass}`}
                                            placeholder={"What would you like to share?"}
                                            defaultValue={postData.message ? postData.message : ''}
                                            onChange={this.props.requestChange}
                                            data-field={`postData.services.${this.props.serviceType}.message`}
                                            data-servicetype={this.props.serviceType}
                                            onPaste={TextareaEvents.onTextareaPaste.bind(this)}
                                            onKeyUp={TextareaEvents.onTextareaKeyUp.bind(this)}>
                                        </textarea>
                                        {serviceImage}
                                    </div>
                                    <div className="service-message-controls">
                                        <div className="text-tools">
                                            <div className="tt-button media-source-chooser">
                                                <MediaSourceMenu
                                                    {...this.props}
                                                    mediaSourceOptions={mediaSourceOptions}/>
                                            </div>
                                            <div className={`tt-button text-count ${errorClass}`}>
                                                {postData.message ? this.props.getCharCount(postData.message, this.props.serviceType) : 0} {linkAlert}
                                            </div>
                                            <div className="tt-button emoji-button" role="button" aria-label="Add emojis" onClick={this.toggleEmojiPicker}>
                                                <i className="fa fa-smile-o"></i>
                                                {(this.state.isEmojiPickerVisible) ? (
                                                    <div className="pt-picker-wrapper" ref={this.emojiPickerRef}>
                                                        <Picker
                                                            onEmojiClick={emojiCallback}
                                                            native={true}/>
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className={categoryButtonClass}
                                                role="button"
                                                aria-label="Choose Category"
                                                title={categoryName}
                                                data-serviceid={this.props.serviceType}
                                                onClick={this.props.showCategoryPicker}>
                                                <i className="icon ion-ios-pricetag"></i>
                                            </div>
                                            <div className={hashtagButtonClass}
                                                role="button"
                                                aira-label="Add generated hashtags to message"
                                                onClick={this.toggleHashtagSelector}>
                                                <i className="icon ion-pound"/>
                                            </div>
                                            {aiHelperButton}
                                        </div>
                                    </div>
                                    {hashtagSelector}
                                </Breakpoint>
                                <Breakpoint mobile only>
                                    <div className="message-group-mobile">
                                        <textarea
                                            ref={this.messageRef}
                                            className={`text-area large ${errorClass}`}
                                            placeholder={"What would you like to share?"}
                                            defaultValue={postData.message ? postData.message : ''}
                                            onChange={this.props.requestChange}
                                            data-field={`postData.services.${this.props.serviceType}.message`}
                                            data-servicetype={this.props.serviceType}
                                            onPaste={TextareaEvents.onTextareaPaste.bind(this)}
                                            onKeyUp={TextareaEvents.onTextareaKeyUp.bind(this)}>
                                        </textarea>
                                        <div className="service-message-controls">
                                            <div className="text-tools">
                                                <div className="tt-button media-source-chooser">
                                                    <MediaSourceMenu
                                                        {...this.props}
                                                        mediaSourceOptions={mediaSourceOptions}/>
                                                </div>
                                                <div className={`tt-button text-count ${errorClass}`}>
                                                    {postData.message ? this.props.getCharCount(postData.message, this.props.serviceType) : 0} {linkAlert}
                                                </div>
                                                <div className="tt-button emoji-button" role="button" aria-label="Add emojis" onClick={this.toggleEmojiPicker}>
                                                    <i className="fa fa-smile-o"></i>
                                                    {(this.state.isEmojiPickerVisible) ? (
                                                        <div className="pt-picker-wrapper" ref={this.emojiPickerRef}>
                                                            <Picker
                                                                onEmojiClick={emojiCallback}
                                                                native={true}/>
                                                        </div>
                                                    ) : null}
                                                </div>
                                                <div className={categoryButtonClass}
                                                    role="button"
                                                    aria-label="Choose Category"
                                                    title={categoryName}
                                                    data-serviceid={this.props.serviceType}
                                                    onClick={this.props.showCategoryPicker}>
                                                    <i className="icon ion-ios-pricetag"></i>
                                                </div>
                                                <div className={hashtagButtonClass}
                                                    role="button"
                                                    aira-label="Add generated hashtags to message"
                                                    onClick={this.toggleHashtagSelector}>
                                                    <i className="icon ion-pound"/>
                                                </div>
                                                {aiHelperButton}
                                            </div>
                                        </div>
                                    </div>
                                    {hashtagSelector}
                                    <div className="image-block-mobile">
                                        {serviceImage}
                                    </div>
                                </Breakpoint>
                            </div>
                        );
                    } else {
                        let isTextArea = false;
                        if (key === "video_description") {
                            isTextArea = true;
                        }

                        return (
                            <div className={`service-${key}`}>
                                <div className="pt-field-label">{LabelLibrary.getStringsForField(this.props.serviceType, key, 'placeholder')}</div>
                                <UncontrolledTextInput
                                    className={`text-input large ${errorClass}`}
                                    value={postData[key] !== undefined ? postData[key] : ''}
                                    onChange={this.props.requestChange}
                                    data-field={`postData.services.${type}.${key}`}
                                    isTextArea={isTextArea}/>
                            </div>
                        );
                    }
                    break;

                case 'enum':
                    let options = [],
                        value = '';

                    let defaultLabel = LabelLibrary.getStringsForField(type, key, 'defaultSelect');

                    if (defaultLabel) {
                        options.push({label: defaultLabel, value: ''});
                    }

                    this.props.serviceInfo.fields[key].values.map((value, i) => {
                        let label = this.props.serviceInfo.fields[key].labels[i];
                        options.push({value, label});
                    });

                    if (postData[key] !== undefined) {
                        value = postData[key];
                    }

                    return (
                        <div>
                            <div className="pt-field-label">{LabelLibrary.getStringsForField(this.props.serviceType, key, 'placeholder')}</div>
                            <StyledSelect
                                value={value}
                                linkedField={`postData.services.${type}.${key}`}
                                onChange={this.props.requestChange}
                                options={options}
                                selectClass="med pt"/>
                        </div>

                    );
                    break;

                case 'custom':
                    if (parseInt(this.props.serviceType) === constants.POST_SVC_GMYBUSINESS) {
                        return (
                            <GoogleSchedule
                                {...this.props}/>
                        );
                    }
                    break;

                default:
                    return field;
            }
        }
    }

    toggleEmojiPicker(e) {

        if (this.emojiPickerRef.current && this.emojiPickerRef.current.contains(e.target)) {
            return;
        }

        this.setState({
            isEmojiPickerVisible: (this.state.isEmojiPickerVisible) ? false : true
        });

    }

    toggleTikTokDisclosure(e) {
        e.preventDefault();

        let nextState = e.currentTarget.dataset.togglestate === "true" ? false : true;

        this.setState({
            tikTokDisclosure: nextState
        });

    }

    toggleHashtagSelector(e) {

        this.setState({
            isHashtagSelectorVisible: (this.state.isHashtagSelectorVisible) ? false : true
        });

    }

    toggleFacebookMediaOptions(e) {
        let field = e.currentTarget.dataset.field,
            nextState = (e.currentTarget.dataset.togglestate === "true") ? false : true,
            nextPublishAsStory = (this.props.postData.publishAsStory !== undefined) ? this.props.postData.publishAsStory : false,
            nextPublishAsReel = (this.props.postData.publishAsReel !== undefined) ? this.props.postData.publishAsReel : false;

        if (field.match("publishAsStory")) {
            nextPublishAsStory = nextState;
            nextPublishAsReel = false;
        }

        if (field.match("publishAsReel")) {
            nextPublishAsReel = nextState;
            nextPublishAsStory = false;
        }

        this.props.requestChange({
            currentTarget: {
                dataset: {
                    field: 'postData.services.2.publishAsStory'
                }
            },
            target: {
                value: nextPublishAsStory
            }
        });

        this.props.requestChange({
            currentTarget: {
                dataset: {
                    field: 'postData.services.2.publishAsReel'
                }
            },
            target: {
                value: nextPublishAsReel
            }
        });

    }

    mediaChecker(types) {
        let typesToDetect = [...types];

        this.props.mediaThumbs.map((thumb) => {
            if (thumb.type.match(/image/) && typesToDetect.indexOf('image') !== -1) {
               typesToDetect.splice(typesToDetect.indexOf('image'), 1);
            } else if (thumb.type.match(/video/) && typesToDetect.indexOf('video') !== -1) {
                typesToDetect.splice(typesToDetect.indexOf('video'), 1);
            }
        });

        return (typesToDetect.length < types.length) ? true : false;
    }

    render() {
        let ServiceIcon = null,
            iconIndex = 0,
            headerFields = null,
            linkPreview = null,
            infoDisplay = null,
            apiErrorDisplay = null,
            errorDisplay = null,
            infoMessages = [],
            constants = this.props.app.config.constants,
            postData = this.props.postData,
            formFields = [],
            fieldOrder = [
                'message',
                'mb_id',
                'title',
                'sentiment',
                'alert_type',
                'schedule',
                'cta_type',
                'link',
                'body',
                'couponCode',
                'termsConditions',
                'photo_title',
                'video_title',
                'video_description',
                'privacy_status',
                'license'
            ],
            serviceType = parseInt(this.props.serviceType),
            tikTokFields = null;

        // we could be in a loading state, return null until safe
        if (this.props.serviceInfo === undefined || this.props.postData === undefined) {
            return null;
        }

        const gatherFields = (formKey) => {
            let fields = [],
                fieldNames = [],
                validator = this.props.posts.postValidators[this.props.serviceType],
                form = validator.forms[formKey],
                formKeys = Object.keys(validator.forms);

            Object.keys(form).map((key) => {
                if (_.isArray(form[key])) {
                    fieldNames = fieldNames.concat(form[key]);
                } else if (_.isObject(form[key])) {
                    if (form[key].any !== undefined) {
                        fieldNames = fieldNames.concat(form[key].any);
                    }
                    if (form[key].all !== undefined) {
                        fieldNames = fieldNames.concat(form[key].all);
                    }
                }
            });

            fieldNames.map((field) => {
                if (formKeys.indexOf(field) !== -1) {
                    fields = fields.concat(gatherFields(field));
                } else {
                    fields.push(field);
                }
            });

            return _.uniq(fields);

        };

        if (serviceType) {
            let code = ConstantMixin.getIconIndexByCode(serviceType);
            ServiceIcon = IconFactory[code];
        }

        // for services that have defined forms (google at the moment)
        if (postData && postData.form !== undefined) {
            formFields = gatherFields(postData.form)
        } else {
            formFields = Object.keys(this.props.serviceInfo.fields);
        }

        /*
         * Said whenever X has a link, with or without media — unlike the block below, which
         * only runs for a post carrying no image of its own. The posting path breaks the
         * domain with a zero-width space so X does not linkify it, and X only builds a card
         * for a link it recognises, so there is never a preview to show either way.
         */
        if (serviceType === constants.POST_SVC_TWITTER &&
            this.props.postData !== undefined &&
            this.props.postData.link) {
            infoMessages.push(`${ConstantMixin.getPostServiceNameByCode(this.props.serviceType)} does not support link previews.`);
        }

        if (this.props.postData !== undefined &&
            this.props.postData.link !== undefined &&
            this.props.postData.link !== '' &&
            (this.props.postData.mb_id === undefined || !this.props.postData.mb_id) &&
            (this.props.postData.hashes !== undefined && this.props.postData.hashes.length === 0)) {
            if (this.props.isLinkLoading && this.props.linklessServices.indexOf(serviceType) === -1) {
                linkPreview = (
                    <LinkPreview
                        {...this.props}
                        loading={true}
                        context={'network'}/>
                );
            } else {
                if (this.props.linklessServices.indexOf(serviceType) !== -1) {
                    if (serviceType === constants.POST_SVC_BLUESKY) {
                        let linkInMessage = this.props.postData.message.includes(this.props.postData.link);
                        linkPreview = (
                            <LinkPreview
                                {...this.props}
                                loading={false}
                                linkInMessage={linkInMessage}
                                postData={this.props.postData}
                                previewData={this.props.linkPreviews[this.props.postData.link]}
                                context={'network'}
                                removeLink={this.props.removeLink}/>
                        );
                    } else if ([
                        constants.POST_SVC_REDDIT,
                        constants.POST_SVC_SLACK,
                        // X says it above, for media and media-less posts alike
                        constants.POST_SVC_TWITTER,
                    ].indexOf(serviceType) === -1) {
                        infoMessages.push(`${ConstantMixin.getPostServiceNameByCode(this.props.serviceType)} does not support link previews.`);
                    }

                } else {
                    linkPreview = (
                        <LinkPreview
                            {...this.props}
                            loading={false}
                            previewData={this.props.linkPreviews[this.props.postData.link]}
                            context={'network'}
                            removeLink={this.props.removeLink}/>
                    );
                }

            }

            if (serviceType === constants.POST_SVC_REDDIT && this.props.postData.form !== 'link_post') {
                // null out the link preview for reddit if the post type isn't right
                linkPreview = null;
            }
        }

        /*
         * X gets no link card, so a post with the QR code on and no image of its own goes
         * out with the code's own card as its image. That card is the preview: a placeholder
         * of its shape while it is drawn, and nothing if it could not be.
         */
        let qrCardKey = this.qrCardKey();
        if (qrCardKey) {
            let qrCard = this.state.qrCard && this.state.qrCard.key === qrCardKey ? this.state.qrCard : null;

            if (!qrCard || qrCard.image) {
                linkPreview = (
                    <div className="pt-link-preview network pt-qr-preview">
                        {qrCard ? (
                            <img src={qrCard.image} alt="The QR card we'll post with the link"/>
                        ) : (
                            <div className="pt-qr-preview-loading" aria-label="Drawing the QR card"/>
                        )}
                    </div>
                );
            }
        }

        const outputError = (error, key) => {
            let errors = [];

            if (_.isObject(error)) {
                let errorKeys = Object.keys(error);
                errorKeys.map((errorKey) => {
                    errors = errors.concat(outputError(error[errorKey], errorKey));
                });

            } else {
                errors.push(
                    <li key={`error-${this.props.serviceType}-${key}`} className="post-error">
                        <i className="icon ion-android-alert"></i>{error}
                    </li>
                );
            }
            return errors;
        }

        if ((this.props.apiErrors[this.props.serviceType] !== undefined &&
            Object.keys(this.props.apiErrors[this.props.serviceType]).length) ||
            (this.props.validationErrors[this.props.serviceType] !== undefined &&
            Object.keys(this.props.validationErrors[this.props.serviceType]).length) || (
             this.props.mediaErrors !== undefined && Object.keys(this.props.mediaErrors).length
            )) {
            errorDisplay = (
                <ul className="pt-service-errors">
                    {(this.props.apiErrors[this.props.serviceType]) ? (
                        Object.keys(this.props.apiErrors[this.props.serviceType]).map((key) => {
                            return outputError(this.props.apiErrors[this.props.serviceType][key], key);
                        })
                    ) : null}
                    {(this.props.validationErrors[this.props.serviceType]) ? (
                        Object.keys(this.props.validationErrors[this.props.serviceType]).map((key) => {
                            return outputError(this.props.validationErrors[this.props.serviceType][key], key);
                        })
                    ) : null}
                    {(_.isArray(this.props.mediaErrors[this.props.serviceType]) || _.isObject(this.props.mediaErrors[this.props.serviceType])) ? (
                        Object.keys(this.props.mediaErrors[this.props.serviceType]).map((key) => {
                            return outputError(this.props.mediaErrors[this.props.serviceType][key], key);
                        })
                    ) : null}
                    {(_.isString(this.props.mediaErrors[this.props.serviceType])) ? (
                        outputError(this.props.mediaErrors[this.props.serviceType], this.props.serviceType)
                    ) : null}
                </ul>
            );
        }

        if ([constants.POST_SVC_INSTAGRAM_BUSINESS, constants.POST_SVC_FACEBOOK].indexOf(serviceType) !== -1) {
            if (postData.publishAsStory !== undefined && postData.publishAsStory && postData.message.trim().length) {
                infoMessages.push(`${ConstantMixin.getPostServiceNameByCode(this.props.serviceType)} will not publish your text with your story.`);
            }
        }

        if (infoMessages.length) {
            infoDisplay = (
                <ul className="pt-service-info">
                    {infoMessages.map((info) => {
                        return (
                            <li key={`info-${this.props.serviceType}`} className="post-info">
                                {info}
                            </li>
                        );
                    })}
                </ul>
            );
        }

        let postOptions = [],
            typeValue = '';

        if (parseInt(this.props.serviceType) === constants.POST_SVC_GMYBUSINESS) {
            postOptions = [
                {value: 'STANDARD', label: 'Update'},
                {value: 'EVENT', label: 'Event'},
                {value: 'OFFER', label: 'Offer'},
                {value: 'ALERT', label: 'Alert'},
            ];
            typeValue = 'STANDARD';

            if (postData !== undefined && postData.form !== undefined) {
                typeValue = postData.form;
            }

            headerFields = (
                <div className="post-type-selector">
                    Google Business Post Type
                    <StyledSelect
                        value={typeValue}
                        onChange={this.props.requestChange}
                        options={postOptions}
                        linkedField={`postData.services.${this.props.serviceType}.form`}
                        selectClass="small gmb"/>
                </div>
            );
        } else if (parseInt(this.props.serviceType) === constants.POST_SVC_REDDIT) {
            postOptions = [
                {value: 'text_post', label: 'Post'},
                {value: 'image_post', label: 'Media'},
                {value: 'link_post', label: 'Shared Link'}
            ];
            typeValue = 'text_post';

            if (postData !== undefined && postData.form !== undefined) {
                typeValue = postData.form;
            }

            headerFields = (
                <div className="post-type-selector reddit">
                    Reddit Post Type
                    <StyledSelect
                        value={typeValue}
                        onChange={this.props.requestChange}
                        options={postOptions}
                        linkedField={`postData.services.${this.props.serviceType}.form`}
                        selectClass="small reddit"/>
                </div>
            );
        } else if (parseInt(this.props.serviceType) === constants.POST_SVC_SNAPCHAT) {
            postOptions = [
                {value: 'story', label: 'Story Post'},
                {value: 'spotlight', label: 'Spotlight Post'}
            ];
            typeValue = 'story';

            if (postData !== undefined && postData.form !== undefined) {
                typeValue = postData.form;
            }

            headerFields = (
                <div className="post-type-selector snapchat">
                    Snapchat Post Type
                    <StyledSelect
                        value={typeValue}
                        onChange={this.props.requestChange}
                        options={postOptions}
                        linkedField={`postData.services.${this.props.serviceType}.form`}
                        selectClass="small snapchat"/>
                </div>
            );
        }

        if (parseInt(this.props.serviceType) === constants.POST_SVC_TIKTOK) {
            let disclosureFields = null,
                disclosureWarning = null;

            if (this.state.tikTokDisclosure) {
                if (postData.brand_content_toggle === true && postData.brand_organic_toggle === false) {
                    disclosureWarning = (
                        <ul className="pt-service-info">
                            <li className="post-info">
                                Your photo/video will be labeled as 'Promotional content'
                            </li>
                        </ul>
                    );
                } else if (postData.brand_organic_toggle === true)  {
                    disclosureWarning = (
                        <ul className="pt-service-info">
                            <li className="post-info">
                                Your photo/video will be labeled as 'Paid partnership'
                            </li>
                        </ul>
                    );
                } else {
                    disclosureWarning = (
                        <ul className="pt-service-info">
                            <li className="post-info">
                                You must choose what kind of content your media presents.
                            </li>
                        </ul>
                    );
                }
                disclosureFields = (
                    <div>
                        {disclosureWarning}
                        <div className="pt-inline-checkbox stacked">
                            <input
                                id="tiktok-brand"
                                type="checkbox"
                                checked={(postData.brand_content_toggle) ? true : false}
                                onChange={this.props.requestChange}
                                data-field={`postData.services.${this.props.serviceType}.brand_content_toggle`}
                                data-disclosure={'brand_content_toggle'}/>
                            <label htmlFor="tiktok-brand">Your Brand <HelpButton which="TTBrandDisclosure"/></label>
                        </div>
                        <div className="pt-inline-checkbox stacked">
                            <input
                                id="tiktok-branded"
                                type="checkbox"
                                checked={(postData.brand_organic_toggle) ? true : false}
                                onChange={this.props.requestChange}
                                data-field={`postData.services.${this.props.serviceType}.brand_organic_toggle`}
                                data-disclosure={'brand_organic_toggle'}/>
                            <label htmlFor="tiktok-branded">Branded Content <HelpButton which="TTBrandedContentDisclosure"/></label>
                        </div>
                        <div className="pt-tiktok-legal">
                            By posting, you agree to TikTok's <a href="https://www.tiktok.com/legal/page/global/music-usage-confirmation/en" target="_blank">Music Usage Confirmation</a>
                        </div>
                    </div>
                );
            }

            let tikTokImageToggles = null,
                tikTokSettings = null;
            if (this.mediaChecker(['image'])) {
                tikTokImageToggles = (
                    <div className="pt-service-field">
                        <div className="pt-inline-checkbox">
                            <input
                                id="tiktok-music"
                                type="checkbox"
                                checked={(postData.disable_music) ? true : false}
                                onChange={this.props.requestChange}
                                data-field={`postData.services.${this.props.serviceType}.disable_music`}/>
                            <label htmlFor="tiktok-music">Disable Music</label>
                        </div>
                    </div>
                );
                tikTokSettings = (
                    <div className="pt-inline-checkboxes">
                        <div className="pt-inline-checkbox">
                            <input
                                id="tiktok-comment"
                                type="checkbox"
                                checked={(postData.disable_comment) ? true : false}
                                onChange={this.props.requestChange}
                                data-field={`postData.services.${this.props.serviceType}.disable_comment`}/>
                            <label htmlFor="tiktok-comment">Disable Comment</label>
                        </div>
                    </div>
                )
            } else {
                tikTokSettings = (
                    <div className="pt-inline-checkboxes">
                        <div className="pt-inline-checkbox">
                            <input
                                id="tiktok-comment"
                                type="checkbox"
                                checked={(postData.disable_comment) ? true : false}
                                onChange={this.props.requestChange}
                                data-field={`postData.services.${this.props.serviceType}.disable_comment`}/>
                            <label htmlFor="tiktok-comment">Disable Comment</label>
                        </div>
                        <div className="pt-inline-checkbox">
                            <input
                                id="tiktok-duet"
                                type="checkbox"
                                checked={(postData.disable_duet) ? true : false}
                                onChange={this.props.requestChange}
                                data-field={`postData.services.${this.props.serviceType}.disable_duet`}/>
                            <label htmlFor="tiktok-duet">Disable Duet</label>
                        </div>
                        <div className="pt-inline-checkbox">
                            <input
                                id="tiktok-stitch"
                                type="checkbox"
                                checked={(postData.disable_stitch) ? true : false}
                                onChange={this.props.requestChange}
                                data-field={`postData.services.${this.props.serviceType}.disable_stitch`}/>
                            <label htmlFor="tiktok-stitch">Disable Stitch</label>
                        </div>
                    </div>
                );
            }

            tikTokFields = (
                <div className="pt-service-field">
                    {tikTokImageToggles}
                    <div className="pt-special-header">Allow users to</div>
                    {tikTokSettings}
                    <div className="pt-inline-toggles">
                        <ToggleControl
                            active={this.state.tikTokDisclosure}
                            labelPlacement="right"
                            activeLabel="Disclose Media Content"
                            inactiveLabel="Disclose Media Content"
                            toggleClass="pt-disclosure-toggle"
                            callback={this.toggleTikTokDisclosure}/>
                    </div>
                    {disclosureFields}
                </div>
            )
        }

        let youtubeFields = null;
        if (parseInt(this.props.serviceType) === constants.POST_SVC_YOUTUBE) {
            let categorySelector = null,
                categoryOptions = [],
                categoryValue = postData.category,
                hasTagError = false;

            if (Object.keys(this.props.socials.categories).length) {
                let keys = Object.keys(this.props.socials.categories);
                    categoryOptions = this.props.socials.categories[keys[0]];

                categorySelector = (
                    <div>
                        <div className="pt-field-label">{LabelLibrary.getStringsForField(this.props.serviceType, 'category', 'placeholder')}</div>
                        <StyledSelect
                            value={categoryValue}
                            linkedField={`postData.services.${this.props.serviceType}.category`}
                            onChange={this.props.requestChange}
                            options={categoryOptions}
                            selectClass="med pt"/>
                    </div>
                );
            }

            if (this.props.validationErrors[this.props.serviceType] !== undefined &&
                this.props.validationErrors[this.props.serviceType] &&
                this.props.validationErrors[this.props.serviceType].tags !== undefined &&
                this.props.validationErrors[this.props.serviceType].tags) {
                hasTagError = true;
            }

            /**

             */

            youtubeFields = (
                <div>
                    <div className="pt-service-field">
                        {categorySelector}
                    </div>
                    <div className="pt-service-field">
                        <div className="pt-field-label">{LabelLibrary.getStringsForField(this.props.serviceType, 'playlists', 'placeholder')}</div>
                        <YouTubePlaylistChooser
                            {...this.props}
                            onChange={this.props.requestChange}
                            playLists={(postData.playLists !== undefined && postData.playLists) ? postData.playLists : []}
                            field={`postData.services.${this.props.serviceType}.playLists`}/>
                    </div>
                    <div className="pt-service-field">
                        <div className="pt-inline-checkboxes">
                            <div className="pt-inline-checkbox">
                                <input
                                    id="youtube-embeddable"
                                    type="checkbox"
                                    checked={(postData.embeddable) ? true : false}
                                    onChange={this.props.requestChange}
                                    data-field={`postData.services.${this.props.serviceType}.embeddable`}/>
                                <label htmlFor="youtube-embeddable">Embeddable</label>
                            </div>
                            <div className="pt-inline-checkbox">
                                <input
                                    id="youtube-mfk"
                                    type="checkbox"
                                    checked={(postData.made_for_kids) ? true : false}
                                    onChange={this.props.requestChange}
                                    data-field={`postData.services.${this.props.serviceType}.made_for_kids`}/>
                                <label htmlFor="youtube-mfk">Made For Kids</label>
                            </div>
                            <div className="pt-inline-checkbox">
                                <input
                                    id="youtube-notifysubs"
                                    type="checkbox"
                                    checked={(postData.notify_subs) ? true : false}
                                    onChange={this.props.requestChange}
                                    data-field={`postData.services.${this.props.serviceType}.notify_subs`}/>
                                <label htmlFor="youtube-notifysubs">Notify Subscribers</label>
                            </div>
                        </div>
                    </div>
                    <div className="pt-service-field">
                        <ToggleControl
                            active={postData.postAsShort ? true : false}
                            labelPlacement="right"
                            activeLabel="Post as Short"
                            inactiveLabel="Post as Short"
                            toggleClass="pt-disclosure-toggle"
                            data-field={`postData.services.${this.props.serviceType}.postAsShort`}
                            callback={this.props.requestChange}/>
                        <div className="pt-tiktok-legal">
                            Shorts are limited to 3 minutes or less in length and may be modified for vertical playback.
                        </div>
                    </div>
                    <div className="pt-service-field">
                        <TagInput
                            {...this.props}
                            tags={postData.tags}
                            hasTagError={hasTagError}
                            updateTags={(tags) => {
                                let tagEvent = {
                                    currentTarget: {
                                        dataset: {
                                            field: `postData.services.${this.props.serviceType}.tags`
                                        }
                                    },
                                    target: {
                                        value: tags
                                    }
                                }

                                this.props.requestChange(tagEvent);
                            }}/>
                    </div>
                </div>
            )
        }

        let storyReelFields = null;
        if (parseInt(this.props.serviceType) === constants.POST_SVC_INSTAGRAM_BUSINESS) {
            storyReelFields = (
                <div className="pt-inline-toggles">
                    <ToggleControl
                        active={postData.publishAsStory ? true : false}
                        labelPlacement="right"
                        activeLabel="Publish as Story"
                        inactiveLabel="Publish as Story"
                        toggleClass="pt-disclosure-toggle"
                        data-field={`postData.services.${this.props.serviceType}.publishAsStory`}
                        callback={this.props.requestChange}/>
                </div>
            );
        }

        if (parseInt(this.props.serviceType) === constants.POST_SVC_FACEBOOK) {
            storyReelFields = (
                <div className="pt-inline-toggles">
                    <ToggleControl
                        controlState={!this.mediaChecker(['image', 'video']) ? 'disabled' : ''}
                        active={postData.publishAsStory ? true : false}
                        labelPlacement="right"
                        activeLabel="Publish as Story"
                        inactiveLabel="Publish as Story"
                        toggleClass="pt-disclosure-toggle"
                        data-field={`postData.services.${this.props.serviceType}.publishAsStory`}
                        callback={this.toggleFacebookMediaOptions}/>
                    <ToggleControl
                        controlState={!this.mediaChecker(['video']) ? 'disabled' : ''}
                        active={postData.publishAsReel ? true : false}
                        labelPlacement="right"
                        activeLabel="Publish as Reel"
                        inactiveLabel="Publish as Reel"
                        toggleClass="pt-disclosure-toggle"
                        data-field={`postData.services.${this.props.serviceType}.publishAsReel`}
                        callback={this.toggleFacebookMediaOptions}/>
                </div>
            )
        }

        /*
         * hasOwnImage() above says why the theme is offered only for a post with no image. That
         * is only ever X: Instagram has no card to theme, because it never posts without an
         * image, and its code always goes on that image.
         */
        let qrCodeField = null;
        const isTwitter = parseInt(this.props.serviceType) === constants.POST_SVC_TWITTER,
            isInstagram = parseInt(this.props.serviceType) === constants.POST_SVC_INSTAGRAM_BUSINESS,
            /*
             * Instagram puts the code on the post's first image and has nowhere else for it, so
             * attached media with no image among it (a reel) can't carry one. With nothing
             * attached the image is the link preview's, which can.
             */
            qrNoImage = isInstagram && this.props.mediaThumbs.length > 0 && !this.mediaChecker(['image']);
        if ((isTwitter || isInstagram) && postData.link) {
            qrCodeField = (
                <div className="pt-qr-fields">
                    <div className="pt-inline-toggles">
                        {/* HelpButton stops its click from reaching the toggle, so the (i) opens
                            the help without flipping the switch. */}
                        <ToggleControl
                            controlState={qrNoImage ? 'disabled' : ''}
                            active={postData.postQrCode && !qrNoImage ? true : false}
                            labelPlacement="right"
                            activeLabel="Add a QR Code for the Link"
                            inactiveLabel="Add a QR Code for the Link"
                            helpButton={<HelpButton which={isInstagram ? 'postQrCodeInstagram' : 'postQrCode'}/>}
                            toggleClass="pt-disclosure-toggle"
                            data-field={`postData.services.${this.props.serviceType}.postQrCode`}
                            callback={this.props.requestChange}/>
                    </div>
                    {/* Below the toggle rather than beside it (Emily): it is a choice about
                        the code the toggle adds, so it reads as belonging to it. Headed and
                        left-aligned the way TikTok's "Allow users to" checkboxes are, under the
                        same label the output settings give it. */}
                    {isTwitter && postData.postQrCode && !hasOwnImage(postData) ? (
                        /*
                         * The card's theme as two named choices rather than a switch whose "off"
                         * does not say what you get. Still the one postDarkQR value: 1 for
                         * dark, 0 for light, which requestChange stores from a checked radio's
                         * value as it is. Unset reads as dark, which is what the server posts.
                         */
                        <div className="pt-qr-theme">
                            <div className="pt-special-header" id={`qr-theme-label-${this.props.serviceType}`}>
                                QR Card Theme<HelpButton which="postDarkQR"/>
                            </div>
                            <div className="pt-inline-radios" role="radiogroup" aria-labelledby={`qr-theme-label-${this.props.serviceType}`}>
                                {[{value: 1, label: 'Dark mode'}, {value: 0, label: 'Light mode'}].map((choice) => {
                                    const field = `postData.services.${this.props.serviceType}.postDarkQR`,
                                        id = `qr-theme-${this.props.serviceType}-${choice.value}`,
                                        dark = isDarkQr(postData);

                                    return (
                                        <div className="pt-inline-checkbox" key={choice.value}>
                                            <input
                                                id={id}
                                                type="radio"
                                                name={field}
                                                value={choice.value}
                                                checked={choice.value === 1 ? dark : !dark}
                                                onChange={this.props.requestChange}
                                                data-field={field}/>
                                            <label htmlFor={id}>{choice.label}</label>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : null}
                </div>
            );
        }

        let headerIcon = (
            <ServiceIcon
                round={true}/>
        );
        if (['edit-queue', 'edit-everqueue'].indexOf(this.props.editMode) !== -1) {
            let social = this.props.socials.list.find(({id}) => { return id === this.props.selectedSocials[this.props.serviceType][0]; })
            if (social) {
                headerIcon = (
                    <div className="tw-root pt-social-button-wrapper">
                        <OutputAvatar
                            network={outputAccountNetwork(social)}
                            initials={initialsFromName(social.name)}
                            avatarUrl={social.icon || social.src}
                            size={40}/>
                    </div>
                );
            }
        }

        return (
            <div className="pt-service-fields">
                <div className="pt-service-header">
                    {headerIcon}
                    {headerFields}
                </div>
                {fieldOrder.map((key) => {
                    if (formFields.indexOf(key) !== -1) {
                        return (
                            <div className="pt-service-field" key={`service-field-${key}`}>
                                {this.fieldGenerator(key)}
                            </div>
                        );
                    }
                })}
                {tikTokFields}
                {youtubeFields}
                {storyReelFields}
                {qrCodeField}
                {linkPreview}
                {infoDisplay}
                {apiErrorDisplay}
                {errorDisplay}
            </div>
        );
    }
}

ServiceFields.displayName = "ServiceFields";

ServiceFields.propTypes = {
    serviceId: PropTypes.number,
    serviceValidator: PropTypes.object,
    sharedData: PropTypes.object
};

ServiceFields.defaultProps = {
    serviceId: 0,
    serviceValidator: {
        fields: [],
        forms: []
    },
    sharedData: {
        text: '',
        link: '',
        mb_id: ''
    }
};

export default ServiceFields;
